// GTM/Meta tracking for the chat flow. Mirrors the event names + payload shape
// used by the calculator (`public/calculator/index.html`) so reports in GA4 +
// Meta Ads + the server-side container at load.ss.fforsikring.dk treat both
// flows as one funnel — only the `variant` field distinguishes them.

import type { LeadState, StepId, Audience } from '../types'

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
    gtag?: (...args: unknown[]) => void
    fbq?: (...args: unknown[]) => void
  }
}

export type TrackEvent =
  | 'forsikringstjek_pageview'
  | 'forsikringstjek_welcome_engaged'
  | 'forsikringstjek_start'
  | 'forsikringstjek_step'
  | 'forsikringstjek_complete'
  | 'forsikringstjek_lead'
  | 'forsikringstjek_abandon'

export interface FbParams {
  fbclid: string
  fbp: string
  fbc: string
  // Ad-level params fra Meta-ad URL (sat via dynamic params i kampagne-URL'en).
  // Bruges til server-side CAPI/CRM-attribution + retargeting via lookalike.
  // Mirror'er calculator-flowets _fbParams (public/calculator/index.html:141)
  // så chat- og calc-leads har identisk attribution-data i Zapier.
  fb_campaign_id: string
  fb_adset_id: string
  fb_ad_id: string
  fb_campaign_name: string
  fb_adset_name: string
  fb_placement: string
  fb_platform: string
}

/**
 * Google Ads + Microsoft Bing click identifiers, captured from URL on landing.
 * - `gclid`: Google Ads click ID. Needed for Enhanced Conversions (browser side)
 *   AND for offline conversion upload back to Google Ads when a lead closes.
 *   Stape's Click ID Restorer power-up persists this in 1st-party storage so it
 *   survives Safari ITP + ad-blockers across the session.
 * - `msclkid`: Microsoft Bing's equivalent. Required by Bing UET Enhanced
 *   Conversions + Microsoft Offline Conversion Import.
 * Captured at landing AND on every readGoogleAdsParams() call so late-loading
 * URL params (rare but possible with SPA route changes) are still picked up.
 */
export interface GoogleAdsParams {
  gclid: string
  msclkid: string
  gclsrc: string
  wbraid: string
  gbraid: string
}

const EMPTY_GADS_PARAMS: GoogleAdsParams = {
  gclid: '', msclkid: '', gclsrc: '', wbraid: '', gbraid: '',
}

export function readGoogleAdsParams(): GoogleAdsParams {
  if (typeof window === 'undefined') return EMPTY_GADS_PARAMS
  const p = new URLSearchParams(window.location.search)
  return {
    gclid: p.get('gclid') ?? '',
    msclkid: p.get('msclkid') ?? '',
    gclsrc: p.get('gclsrc') ?? '',
    wbraid: p.get('wbraid') ?? '',
    gbraid: p.get('gbraid') ?? '',
  }
}

export interface TtParams {
  ttclid: string
  ttp: string
  // Ad-level params fra TikTok-ad URL. TikTok bruger "adgroup" hvor Meta bruger
  // "adset", og har et separat creative-niveau under ad. Vi følger TikTok's egen
  // terminologi så cross-reference mod TikTok Ads Manager er ligetil. Capture'd
  // hvis TikTok's URL-makroer er sat op i kampagne-URL'en — se
  // https://ads.tiktok.com/help/article/dynamic-tracking-url-parameters
  tt_campaign_id: string
  tt_adgroup_id: string
  tt_ad_id: string
  tt_creative_id: string
  tt_campaign_name: string
  tt_adgroup_name: string
  tt_placement: string
}

function getCookie(name: string): string {
  if (typeof document === 'undefined') return ''
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : ''
}

const EMPTY_FB_PARAMS: FbParams = {
  fbclid: '', fbp: '', fbc: '',
  fb_campaign_id: '', fb_adset_id: '', fb_ad_id: '',
  fb_campaign_name: '', fb_adset_name: '', fb_placement: '', fb_platform: '',
}

const EMPTY_TT_PARAMS: TtParams = {
  ttclid: '', ttp: '',
  tt_campaign_id: '', tt_adgroup_id: '', tt_ad_id: '', tt_creative_id: '',
  tt_campaign_name: '', tt_adgroup_name: '', tt_placement: '',
}

export function readFbParams(): FbParams {
  if (typeof window === 'undefined') return EMPTY_FB_PARAMS
  const p = new URLSearchParams(window.location.search)
  const fbclid = p.get('fbclid') ?? ''
  const fbcCookie = getCookie('_fbc')
  return {
    fbclid,
    fbp: getCookie('_fbp'),
    fbc: fbcCookie || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : ''),
    fb_campaign_id: p.get('campaign_id') ?? '',
    fb_adset_id: p.get('adset_id') ?? '',
    fb_ad_id: p.get('ad_id') ?? '',
    fb_campaign_name: p.get('campaign_name') ?? '',
    fb_adset_name: p.get('adset_name') ?? '',
    fb_placement: p.get('placement') ?? '',
    fb_platform: p.get('platform') ?? '',
  }
}

/**
 * Læs TikTok-attribution-params samme sted som /api/lead-submit'et bruger dem.
 *
 * Click-ID (ttclid) kommer som ?ttclid=... på TikTok-ad-URL'en. Det er det
 * vigtigste signal til TikTok Events API: uden ttclid kan TikTok ikke matche
 * en server-side conversion tilbage til det rigtige klik, og deres optimering
 * får ikke et brugbart konverteringsignal.
 *
 * _ttp-cookien sættes af TikTok-pixel'en (TIKTOK2026, indlæst via GTM-server-
 * side container på ss.fforsikring.dk). Vi bruger den både som advanced-match-
 * signal i CAPI OG til dedup mellem browser-pixel og server-side conversion.
 *
 * Ad-niveau-IDs (tt_campaign_id/tt_adgroup_id/tt_ad_id/tt_creative_id) hentes
 * via TikTok's URL-makroer. Disse SKAL konfigureres i TikTok Ads Manager →
 * "Tracking URLs" på selve annoncen, fx:
 *   ?ttclid={{ttclid}}&tt_campaign_id=__CID__&tt_adgroup_id=__AID__
 *      &tt_ad_id=__CTADID__&tt_creative_id=__CRID__
 *      &tt_campaign_name=__CAMPAIGN_NAME__&tt_adgroup_name=__AID_NAME__
 *      &tt_placement=__PLACEMENT__
 * — ellers ankommer felterne tomme. Mange annoncer i jeres setup leverer pt.
 * "__CAMPAIGN_NAME__" som litteral tekst (placeholder ikke udfyldt af TikTok),
 * hvilket indikerer macros er sat op men dynamic-tracking ikke er aktiveret.
 */
export function readTtParams(): TtParams {
  if (typeof window === 'undefined') return EMPTY_TT_PARAMS
  const p = new URLSearchParams(window.location.search)
  return {
    ttclid: p.get('ttclid') ?? '',
    ttp: getCookie('_ttp'),
    tt_campaign_id: p.get('tt_campaign_id') ?? '',
    tt_adgroup_id: p.get('tt_adgroup_id') ?? '',
    tt_ad_id: p.get('tt_ad_id') ?? '',
    tt_creative_id: p.get('tt_creative_id') ?? '',
    tt_campaign_name: p.get('tt_campaign_name') ?? '',
    tt_adgroup_name: p.get('tt_adgroup_name') ?? '',
    tt_placement: p.get('tt_placement') ?? '',
  }
}

export function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  if (typeof window === 'undefined') return 'desktop'
  const ua = navigator.userAgent
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) return 'tablet'
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile'
  return window.innerWidth < 1024 ? 'tablet' : 'desktop'
}

/**
 * Push to dataLayer + fire gtag + Meta Pixel. Always tagged with `variant: 'chat'`
 * (outer A/B test) and the caller-supplied `audience` (target segment) so the
 * server-side container can route + the analytics surface can split both axes.
 */
export function track(event: TrackEvent, audience: Audience, data: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return
  const payload = {
    event,
    variant: 'chat',
    audience,
    ...data,
    timestamp: new Date().toISOString(),
  }

  try {
    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push(payload)
  } catch { /* GTM not loaded — ignore */ }

  try {
    if (typeof window.gtag === 'function') window.gtag('event', event, payload)
  } catch { /* gtag not configured — ignore */ }

  // Meta-pixel-fyring håndteres af GTM tag 82 (Meta Pixel - Lead) som lytter på
  // dataLayer 'submit_lead_form' og fyrer fbq('track','CompleteRegistration',{},{eventID})
  // med advanced matching + dedup mod sGTM Meta CAPI (tag 22). Inline fbq her ville
  // duplikere event uden eventID → dobbelt-tælling i Meta. TikTok håndteres af tag 111.
}

/**
 * Lead-scoring formula adapted from calculator's `calculateLeadScore`. Used so
 * Google/Meta optimization targets the same quality signal in both flows.
 *
 *   1 base point
 *   + 2 if estimated annual premium >= 12 000 kr
 *   + 1 if estimated annual premium >= 8 000 kr
 *   + 1 if user has a car (any insurance other than 'ingen')
 *   + 0.5 if age 18-21
 *   capped at 5
 */
export function calculateLeadScore(state: LeadState): number {
  let score = 1
  const annualPremium = (state.currentPriceVal ?? 0) * 12
  if (annualPremium >= 12000) score += 2
  else if (annualPremium >= 8000) score += 1

  const insurances = state.insurances ?? []
  const hasNoInsurance = insurances.includes('ingen')
  if (!hasNoInsurance) score += 1

  const ageNum = parseInt(state.age ?? '', 10)
  if (Number.isFinite(ageNum) && ageNum >= 18 && ageNum <= 21) score += 0.5

  return Math.min(5, Math.round(score))
}

/* ─── Convenience wrappers used from the chat runner ─── */

export function trackPageview(audience: Audience): void {
  track('forsikringstjek_pageview', audience, { device: getDeviceType() })
}

// Fyres når brugeren klikker "Find min pris" på welcome-step. Adskilt fra
// forsikringstjek_start (som først fyres ved første step-submit) så vi kan måle
// drop-off mellem pageview og første reelle interaktion separat.
export function trackWelcomeEngaged(audience: Audience): void {
  track('forsikringstjek_welcome_engaged', audience, { device: getDeviceType() })
}

export function trackStart(audience: Audience): void {
  track('forsikringstjek_start', audience, { device: getDeviceType() })
}

export function trackStep(audience: Audience, stepIndex: number, stepId: StepId, answer: unknown): void {
  track('forsikringstjek_step', audience, {
    step: stepIndex,
    step_id: stepId,
    answer,
    device: getDeviceType(),
  })
}

export function trackComplete(audience: Audience, state: LeadState, completionSeconds: number): void {
  track('forsikringstjek_complete', audience, {
    lead_score: calculateLeadScore(state),
    completion_seconds: completionSeconds,
    age: state.age,
    postnr: state.postnr,
    device: getDeviceType(),
  })
}

export function trackLead(audience: Audience, sessionId: string, state: LeadState, completionSeconds: number): void {
  const fb = readFbParams()
  const tt = readTtParams()
  track('forsikringstjek_lead', audience, {
    // event_id = sessionId (= leadId server-side). DataLayer-konsumenter
    // (GTM-tags for TikTok-pixel + Meta-pixel) skal læse dette og videresende
    // det som `event_id` på begge pixel-fyringer. Det giver TikTok og Meta
    // mulighed for at dedup'e mod vores server-side CAPI-leverancer (queue.ts
    // sendTikTokConversion bruger samme leadId), så vi ikke får dobbelt-
    // tællede CompleteRegistration-events i deres dashboards.
    event_id: sessionId,
    lead_id: sessionId,
    lead_score: calculateLeadScore(state),
    completion_seconds: completionSeconds,
    age: state.age,
    postnr: state.postnr,
    by: state.by,
    insurances: state.insurances,
    has_existing: !(state.insurances ?? []).includes('ingen'),
    monthly_premium: state.currentPriceVal,
    annual_premium: (state.currentPriceVal ?? 0) * 12,
    content_name: audience === 'aeldre' ? 'bilforsikring_aeldre' : 'bilforsikring_ung',
    device: getDeviceType(),
    fbp: fb.fbp,
    fbc: fb.fbc,
    fbclid: fb.fbclid,
    ttclid: tt.ttclid,
    ttp: tt.ttp,
  })
}

/**
 * Fyres EFTER POST til /api/lead er accepteret. Adskilt fra forsikringstjek_lead
 * (som fyrer optimistisk *før* POST i calc-flowet for at sikre marketing-pixel-
 * dækning ved hiccups) så GTM kan binde Google Ads Enhanced Conversions, Bing
 * UET form-konvertering og GA4 "Udfyldt formular" til en bekræftet submission
 * i stedet for et håbefuldt intent. Indeholder PII (phone) til enhanced match —
 * GTM UPD-variablen plukker `phone` fra denne event via DLV.
 */
export function trackSubmitLeadForm(audience: Audience, sessionId: string, state: LeadState): void {
  if (typeof window === 'undefined') return
  const gads = readGoogleAdsParams()
  const fb = readFbParams()
  const tt = readTtParams()
  // PII passes through dataLayer in plaintext — Stape sGTM (load.ss.fforsikring.dk)
  // SHA-256 hashes server-side before forwarding to Meta CAPI / TikTok EAPI /
  // Google Ads Enhanced Conversions / Bing UET Enhanced Conversions. Do NOT
  // pre-hash here; double-hashing breaks match-rate. See sGTM Meta tag 22 user_data
  // mapping (em/ph/fn/ln/zp/ct/country).
  const payload: Record<string, unknown> = {
    event: 'submit_lead_form',
    variant: 'chat',
    audience,
    event_id: sessionId,
    lead_id: sessionId,
    user_id: sessionId,
    country: 'DK',
    timestamp: new Date().toISOString(),
  }
  if (state.firstName) payload.first_name = state.firstName.trim()
  if (state.lastName) payload.last_name = state.lastName.trim()
  if (state.phone) payload.phone = state.phone
  if (state.postnr) payload.zip_code = state.postnr
  if (state.by) payload.city = state.by
  // Click IDs — for Stape Click ID Restorer + Enhanced Conversions
  if (gads.gclid) payload.gclid = gads.gclid
  if (gads.msclkid) payload.msclkid = gads.msclkid
  if (gads.wbraid) payload.wbraid = gads.wbraid
  if (gads.gbraid) payload.gbraid = gads.gbraid
  if (fb.fbp) payload.fbp = fb.fbp
  if (fb.fbc) payload.fbc = fb.fbc
  if (tt.ttclid) payload.ttclid = tt.ttclid
  if (tt.ttp) payload.ttp = tt.ttp
  try {
    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push(payload)
  } catch { /* GTM not loaded — ignore */ }
}

/**
 * Sender sessionId som GA4 user_id for cross-session + cross-device stitching.
 * Skal kaldes EFTER ensureSession har reconciled mod server (så det canonical
 * sessionId bruges, ikke et tilfældigt client-genereret UUID). Push'er både til
 * dataLayer (så GTM-tags kan læse via DLV) OG via gtag('set') (så efterfølgende
 * gtag()-events automatisk inkluderer user_id uden eksplicit param).
 */
export function setGa4UserId(sessionId: string): void {
  if (typeof window === 'undefined' || !sessionId) return
  try {
    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push({ user_id: sessionId })
  } catch { /* GTM not loaded — ignore */ }
  try {
    if (typeof window.gtag === 'function') window.gtag('set', { user_id: sessionId })
  } catch { /* gtag not configured — ignore */ }
}

export function trackAbandon(audience: Audience, lastStepId: StepId, stepsCompleted: number, secondsSpent: number): void {
  track('forsikringstjek_abandon', audience, {
    last_step: lastStepId,
    steps_completed: stepsCompleted,
    seconds_spent: secondsSpent,
    device: getDeviceType(),
  })
}
